import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportCardPage = () => {
  const { studentId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/grades/students/${studentId}/full`, {
        headers: getAuthHeader()
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report card');
      navigate('/grades');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-8">
        <Skeleton className="h-10 w-48 mb-4" />
        <Skeleton className="h-6 w-32 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white p-8 text-center">
        <h1 className="text-2xl font-medium text-gray-900 mb-4">Student not found</h1>
        <Button onClick={() => navigate('/grades')}>Back to Grades</Button>
      </div>
    );
  }

  const { student, subjects, overall_gpa } = data;
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Controls - hidden during print */}
      <div className="print:hidden bg-white border-b p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => navigate(`/grades/${studentId}`)}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Grades
          </Button>
          <Button onClick={handlePrint} className="gap-2" data-testid="print-report-card">
            <Printer className="w-4 h-4" />
            Print Report Card
          </Button>
        </div>
      </div>

      {/* Report Card Content */}
      <div className="max-w-4xl mx-auto p-8 print:p-0 print:max-w-none">
        <div className="bg-white shadow-lg print:shadow-none p-8 print:p-12">
          {/* Header */}
          <div className="text-center mb-8 border-b-2 border-gray-800 pb-6">
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">
              HomeClass Academy
            </h1>
            <h2 className="text-xl font-semibold text-gray-700">Report Card</h2>
          </div>

          {/* Student Info */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <p className="text-lg">
                <span className="font-semibold">Student:</span>{' '}
                {student.first_name} {student.last_name}
              </p>
              <p className="text-lg">
                <span className="font-semibold">Grade Level:</span>{' '}
                {student.grade_level}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg">
                <span className="font-semibold">Date:</span> {currentDate}
              </p>
            </div>
          </div>

          {/* Grades Table */}
          <table className="w-full border-collapse mb-8">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold">
                  Subject
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-24">
                  Average
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-24">
                  Letter Grade
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center font-semibold w-20">
                  GPA
                </th>
              </tr>
            </thead>
            <tbody>
              {subjects.length > 0 ? (
                subjects.map((subject) => (
                  <tr key={subject.id}>
                    <td className="border border-gray-300 px-4 py-3">
                      {subject.subject_name}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {subject.average.toFixed(1)}%
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center font-bold">
                      {subject.letter_grade}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {subject.gpa.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                    No subjects recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Overall GPA */}
          <div className="flex justify-end mb-8">
            <div className="bg-gray-100 px-6 py-4 rounded-lg">
              <p className="text-lg">
                <span className="font-semibold">Overall GPA:</span>{' '}
                <span className="text-2xl font-bold">{overall_gpa.toFixed(2)}</span>
              </p>
            </div>
          </div>

          {/* Grading Scale */}
          <div className="border-t pt-6">
            <h3 className="font-semibold mb-3">Grading Scale</h3>
            <div className="grid grid-cols-6 gap-2 text-sm text-gray-600">
              <div>A+ 97-100</div>
              <div>A 93-96</div>
              <div>A- 90-92</div>
              <div>B+ 87-89</div>
              <div>B 83-86</div>
              <div>B- 80-82</div>
              <div>C+ 77-79</div>
              <div>C 73-76</div>
              <div>C- 70-72</div>
              <div>D 60-69</div>
              <div>F Below 60</div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t text-center text-gray-500 text-sm">
            <p>This report card was generated by HomeClass Academy</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:p-12 {
            padding: 3rem !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReportCardPage;
